import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { MailchimpApiResponse, MailchimpParameters } from '@tmh-website/services/form.service';
import { Values, WhitepaperFormValidation, submitForm } from '@tmh-website/services/whitepaper.service';
import { Breakpoint } from '@tmh-website/shared/const';
import Button, { ButtonHoverThemeColor, ButtonThemeColor } from '@tmh-website/ui/Button';
import { FormElementThemeColor } from '@tmh-website/ui/FormElements';
import { ErrorMessage, SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';

import * as Styled from './WhitepaperForm.styles';

export interface WhitepaperFormProps {
  formParameters: MailchimpParameters;
  formElementsTheme: FormElementThemeColor;
  buttonThemeColor: ButtonThemeColor;
  buttonHoverThemeColor: ButtonHoverThemeColor;
}

const WhitepaperForm = ({
  formParameters,
  formElementsTheme,
  buttonThemeColor,
  buttonHoverThemeColor,
}: WhitepaperFormProps) => {
  const { t } = useTranslation();

  const {
    mutateAsync: submitMutation,
    isSuccess,
    isError,
  } = useMutation<MailchimpApiResponse, Error, Values>(values => submitForm(values, formParameters));

  return (
    <Styled.FormContainer>
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          company: '',
          email: '',
        }}
        validationSchema={WhitepaperFormValidation}
        onSubmit={values => submitMutation(values)}>
        {({ isSubmitting }) => (
          <Form style={{}} data-cy='whitepaperForm'>
            <>
              <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
                <Field
                  as={Input}
                  themeColor={formElementsTheme}
                  label={`${t('global:label.firstName')}*`}
                  name='firstName'
                  autoComplete='given-name'
                />
                <Field
                  as={Input}
                  themeColor={formElementsTheme}
                  label={`${t('global:label.lastName')}*`}
                  name='lastName'
                  autoComplete='family-name'
                />
              </FormSection>
              <FormSection numberOfColumns={1}>
                <Field
                  as={Input}
                  themeColor={formElementsTheme}
                  label={`${t('global:label.company')}*`}
                  name='company'
                  autoComplete='organization'
                />
              </FormSection>
              <FormSection numberOfColumns={1}>
                <Field
                  as={Input}
                  themeColor={formElementsTheme}
                  label={`${t('global:label.workEmail')}*`}
                  name='email'
                  autoComplete='email'
                  type='email'
                />
              </FormSection>
              <div>
                <Button
                  themeColor={buttonThemeColor}
                  hoverThemeColor={buttonHoverThemeColor}
                  isLoading={isSubmitting}
                  disabled={isSuccess}>
                  {t('global:whitepaper.downloadNow')}
                </Button>
              </div>

              {isSuccess && <SuccessMessage marginTop>{t('global:whitepaper.thanksMessage')}</SuccessMessage>}
              {isError && <ErrorMessage marginTop>{t('global:errorUnknown')}</ErrorMessage>}
            </>
          </Form>
        )}
      </Formik>
    </Styled.FormContainer>
  );
};

export default WhitepaperForm;
